import FullPageLoader from 'Components/FullPageLoader/FullPageLoader'
import { useGetCodespacesQuery } from 'Services/ControlDataManagement/Features/Codespace/codespaceApi'
import type { Codespace } from 'Services/ControlDataManagement/Features/Codespace/types'
import { useGetAllAuthoritiesQuery } from 'Services/common/Features/Authority/authorityApi'
import type { Authority } from 'Services/common/Features/Authority/types'
import React, { createContext, useCallback, useContext } from 'react'

interface AuthorityContextValue {
  authorities: Authority[] | undefined
  isAuthoritiesLoading: boolean
  getAuthorityNameByCode: (id: string) => string
  codespaces: Codespace[]
  isCodespacesLoading: boolean
  getCodespaceNameByCode: (id: string) => string
}

const AuthorityContext = createContext<AuthorityContextValue | undefined>(undefined)

export const AuthorityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: authorities, isLoading: isAuthoritiesLoading } = useGetAllAuthoritiesQuery()
  const { data: { resources: codespaces } = { resources: [] }, isLoading: isCodespacesLoading } =
    useGetCodespacesQuery({ bypass_pagination: true })

  const getAuthorityNameByCode = useCallback(
    (code: string) => {
      const authority = authorities?.find(authority => authority.code === code)
      return authority ? authority.name : '?'
    },
    [authorities]
  )

  const getCodespaceNameByCode = useCallback(
    (code: string) => {
      const codespace = codespaces.find(codespace => codespace.codespace_id === code)
      return codespace ? codespace.description : '?'
    },
    [codespaces]
  )

  const contextValue = React.useMemo(
    () => ({
      authorities,
      isAuthoritiesLoading,
      getAuthorityNameByCode,
      codespaces,
      isCodespacesLoading,
      getCodespaceNameByCode
    }),
    [
      authorities,
      isAuthoritiesLoading,
      codespaces,
      isCodespacesLoading,
      getAuthorityNameByCode,
      getCodespaceNameByCode
    ]
  )

  // Display a full page loader while loading codespaces or authorities since these are required for initializing the layout
  if (isCodespacesLoading || isAuthoritiesLoading) {
    return <FullPageLoader />
  }

  return <AuthorityContext.Provider value={contextValue}>{children}</AuthorityContext.Provider>
}

export const useAuthorityContext = (): AuthorityContextValue => {
  const context = useContext(AuthorityContext)
  if (!context) {
    throw new Error('useAuthorityContext must be used within an AuthorityProvider')
  }
  return context
}
